import { mutationOptions, type QueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { api } from "./apiClient";
import { FORMS_API_BASE_ENDPOINT } from "./constants";
import { logError } from "./logError";
import { hasErrorCode } from "./errors";

const BASE_ENDPOINT = `${FORMS_API_BASE_ENDPOINT}/files`;

const UPLOAD_FILE_MUTATION_KEY = ["uploadFile"];

export const isFileUploading = (queryClient: QueryClient) =>
  queryClient.isMutating({ mutationKey: UPLOAD_FILE_MUTATION_KEY }) > 0;

export type FileOwner = {
  type:
    | "Registration"
    | "ProcessorRegistration"
    | "RegistrationAutoDpiaPreAssessment"
    | "System"
    | "PartnerCompliance"
    | "Dpia"
    | "Partner";
  id: string;
};

const UploadedFileSchema = z.object({
  id: z.string(),
  fileName: z.string(),
  size: z.number(),
});
export type UploadedFile = z.infer<typeof UploadedFileSchema>;

export const uploadFileMutationOptions = () =>
  mutationOptions({
    mutationKey: UPLOAD_FILE_MUTATION_KEY,
    mutationFn: async ({
      owner,
      questionId,
      file,
      signal,
    }: {
      owner: FileOwner;
      questionId?: string;
      file: File;
      signal: AbortSignal;
    }) => {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("title", file.name);
      if (questionId) formData.append("questionId", questionId);
      return api.postFile(`${BASE_ENDPOINT}/${owner.type}/${owner.id}`, formData, {
        signal,
        schema: UploadedFileSchema,
      });
    },
    onError: error => {
      const isCancelledByUser = error instanceof DOMException && error.name === "AbortError";
      if (!isCancelledByUser) logError(error, { loggedFrom: "QueryClient" });
    },
  });

export const downloadFileMutationOptions = () =>
  mutationOptions({
    mutationFn: (fileId: string) => api.get(`${BASE_ENDPOINT}/${fileId}/content`, { schema: z.instanceof(Response) }),
    retry: (failureCount, error) => hasErrorCode(error, "FILE_SCAN_PENDING") && failureCount < 10,
    retryDelay: 2000,
  });
